import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useUserStore } from "@/stores/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const databaseService = serviceHost.getDatabaseService();

const specialAttributeRepository =
  repositoryHost.getSpecialAttributeRepository(databaseService);

type CreateSpecialAttributeParams = Parameters<
  typeof specialAttributeRepository.create
>[0];
type UpdateSpecialAttributeParams = Parameters<
  typeof specialAttributeRepository.update
>[0];
type DeleteSpecialAttributeParams = Parameters<
  typeof specialAttributeRepository.delete
>[0];

const SPECIAL_ATTRIBUTES_QUERY_KEY = ["special-attributes", "get"] as const;
const SPECIAL_ATTRIBUTES_STALE_TIME_MS = 5 * 60 * 1000;

export const useSpecialAttributes = () => {
  return useQuery({
    queryKey: SPECIAL_ATTRIBUTES_QUERY_KEY,
    queryFn: async () =>
      await specialAttributeRepository.getAll({
        orderBy: { field: "name", direction: "asc" },
      }),
    staleTime: SPECIAL_ATTRIBUTES_STALE_TIME_MS,
  });
};

export const useCreateSpecialAttribute = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<string, Error, CreateSpecialAttributeParams>({
    mutationKey: ["special-attribute", "create"],
    mutationFn: async (params: CreateSpecialAttributeParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to create special attributes",
        );
      }
      return specialAttributeRepository.create(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPECIAL_ATTRIBUTES_QUERY_KEY });
    },
  });
};

export const useUpdateSpecialAttribute = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, UpdateSpecialAttributeParams>({
    mutationKey: ["special-attribute", "update"],
    mutationFn: async (params: UpdateSpecialAttributeParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to update special attributes",
        );
      }
      return specialAttributeRepository.update(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPECIAL_ATTRIBUTES_QUERY_KEY });
    },
  });
};

export const useDeleteSpecialAttribute = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, DeleteSpecialAttributeParams>({
    mutationKey: ["special-attribute", "delete"],
    mutationFn: async (params: DeleteSpecialAttributeParams) => {
      if (!hasFullAccess()) {
        throw new Error(
          "Access denied: Full access required to delete special attributes",
        );
      }
      return specialAttributeRepository.delete(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPECIAL_ATTRIBUTES_QUERY_KEY });
    },
  });
};
